import React, { useEffect, useState, useCallback } from "react";
import { Activity, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import InjuryFormModal from "./InjuryFormModal";
import {
  SEVERITY_LABEL,
  SEVERITY_COLOR,
  STATUS_LABEL,
  STATUS_COLOR,
  typeLabel,
  bodyPartLabel,
} from "../lib/injuryLabels";

/**
 * Injuries panel for a single athlete. Shows active + recovering rows up top,
 * resolved history collapsed below. Click any row to edit / mark resolved.
 *
 * Props:
 *   sailor    : { id, name }
 *   constants : injury constants object loaded once at app level
 *   onChanged : fn() — fires after any add/edit/delete so the parent
 *               (typically App) can refresh sidebar indicators
 */
export default function InjuriesPanel({ sailor, constants, onChanged }) {
  const [items, setItems] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!sailor) return;
    setLoading(true);
    try {
      const r = await api.get(`/injuries?sailor_id=${sailor.id}`);
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, [sailor]);

  useEffect(() => {
    load();
  }, [load]);

  const active = items.filter((i) => i.status !== "resolved");
  const resolved = items.filter((i) => i.status === "resolved");

  const handleSaved = () => {
    load();
    onChanged?.();
  };

  return (
    <div className="card p-4 md:p-5" data-testid="injuries-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Activity size={11} className="text-rose-300" /> Injuries &amp; first-aid log
          </div>
          <div className="text-sm text-slate-300 mt-0.5">
            {active.length === 0 ? (
              <span className="text-emerald-300">No active issues</span>
            ) : (
              <span className="text-rose-300">
                {active.length} active issue{active.length === 1 ? "" : "s"}
              </span>
            )}
            {resolved.length > 0 && (
              <span className="text-slate-500"> · {resolved.length} resolved on record</span>
            )}
          </div>
        </div>
      </div>

      {loading && items.length === 0 && (
        <div className="text-sm text-slate-500 py-2">Loading…</div>
      )}

      {/* Active list */}
      {active.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {active.map((inj) => (
            <InjuryRow key={inj.id} injury={inj} onClick={() => setEditing(inj)} />
          ))}
        </div>
      )}

      {/* Resolved (collapsed) */}
      {resolved.length > 0 && (
        <>
          <button
            onClick={() => setShowResolved((v) => !v)}
            className="text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
            data-testid="injury-toggle-resolved"
          >
            <Clock size={11} />
            {showResolved ? "Hide" : "Show"} resolved history ({resolved.length})
          </button>
          {showResolved && (
            <div className="space-y-1 mt-2" data-testid="injury-resolved-list">
              {resolved.map((inj) => (
                <InjuryRow key={inj.id} injury={inj} onClick={() => setEditing(inj)} dim />
              ))}
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className="text-center py-6 border border-dashed border-slate-700 rounded-lg" data-testid="injury-empty-state">
          <div className="text-sm text-slate-400">No injuries on record. Use &ldquo;Log injury/illness&rdquo; in the toolbar above.</div>
        </div>
      )}

      <InjuryFormModal
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
        sailor={sailor}
        injury={editing}
        constants={constants}
      />
    </div>
  );
}

function InjuryRow({ injury, onClick, dim }) {
  const sev = SEVERITY_COLOR[injury.severity] || "#94A3B8";
  const stat = STATUS_COLOR[injury.status] || "#94A3B8";
  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-md px-3 py-2 border transition flex items-center gap-3 ${
        dim ? "border-slate-800 hover:border-slate-700 opacity-70" : "border-[#1E304B] hover:border-cyan-500/40 bg-[#0B1627]"
      }`}
      data-testid={`injury-row-${injury.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-slate-100 text-sm font-display">
            {(injury.type === "other" && injury.type_other) ? injury.type_other : typeLabel(injury.type)}
            {" — "}
            {(injury.body_part === "general" && injury.body_part_other) ? injury.body_part_other : bodyPartLabel(injury.body_part)}
          </span>
          <span
            className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data"
            style={{
              background: `${sev}22`,
              color: sev,
              border: `1px solid ${sev}55`,
            }}
          >
            {SEVERITY_LABEL[injury.severity] || injury.severity}
          </span>
          {injury.rest_required && injury.status !== "resolved" && (
            <span
              className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data"
              style={{ background: "#F59E0B22", color: "#F59E0B", border: "1px solid #F59E0B55" }}
            >
              On rest{injury.days_off_training ? ` · ${injury.days_off_training}d` : ""}
            </span>
          )}
        </div>
        {injury.description && (
          <div className="text-[11px] text-slate-400 mt-0.5 truncate">{injury.description}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          {injury.status === "resolved" ? "Resolved" : "Onset"}
        </div>
        <div className="font-mono-data text-xs text-slate-200">
          {fmtDate(injury.status === "resolved" ? injury.resolved_date : injury.onset_date)}
        </div>
      </div>
      <span
        className="shrink-0 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-mono-data flex items-center gap-1"
        style={{ background: `${stat}22`, color: stat, border: `1px solid ${stat}55` }}
      >
        {injury.status === "resolved" ? <CheckCircle2 size={9} /> : <AlertCircle size={9} />}
        {STATUS_LABEL[injury.status] || injury.status}
      </span>
    </button>
  );
}
